'use client'

import { useEffect, useState } from 'react'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import { useRouteLoaderData, useNavigate, useParams } from 'react-router-dom'
import { GlobalLoadingBar } from '@repo/ui/GlobalLoadingBar'
import { useLoadingStore } from '@repo/store/useLoadingStore'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useToastStore } from '@repo/store/useToastStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import IframePage from '@repo/app-assets/IframePage'
import { AuthLayout } from '@repo/app-assets/AuthLayout'
import { DefaultLayout } from '@repo/app-assets/DefaultLayout'
import { RouteGuard } from '@repo/app-assets/RouteGuard'
import { useNavBlockerForOffline } from '@repo/app-assets/useNavBlockerForOffline'
import { mixpanelTrackPageView } from '@repo/app-assets/Mixpanel'
import { ConfirmMountPoint } from '@repo/ui/ConfirmMountPoint'
import { PageErrorBoundary } from '@repo/app-assets/PageErrorBoundary'
import { getCompanyCodeFromUrl, getDatabaseSuffixFromUrl } from '@repo/utils/urlParsing'
import { reactPlugin } from '@repo/app-assets/ApplicationInsights'
import { CheckForLatestVersion } from '../CheckForLatestVersion'

export const RootLayout = () => {
  const { isLoading } = useLoadingStore()
  const { iframeIsLoggedIn } = useAuthenticationStore()
  const { routeId, urlSegments, iframeSrc, breadcrumbs } = useUrlStore()
  const { environmentName, isLocal, subdomain, databaseSuffix } = useGlobalSettingsStore()
  const { allowedMenuIds } = useMenuItemsStore()
  const routeLoaderData = useRouteLoaderData(routeId)
  const iframeVisible = iframeIsLoggedIn && (routeLoaderData?.iframeVisible || false)
  const { hash, host, pathname } = window.location
  const [mixpanelRoute, setMixpanelRoute] = useState(routeId)
  const feedbackFormUrl = 'https://pracctice.atlassian.net/jira/software/c/form/1a532abf-f825-431c-9766-54801ce85e36'
  const navigate = useNavigate()
  const params = useParams()
  const { reset: resetToasts } = useToastStore()

  useNavBlockerForOffline()

  useEffect(() => {
    // on page refresh, reset toasts so we don't see errors already shown
    resetToasts()
  }, [resetToasts])

  useEffect(() => {
    setMixpanelRoute(routeId)
  }, [routeId])

  useEffect(() => {
    const logUnhandledErrors = (event) => {
      const error = event.reason

      const ignoreErrors = ['Not logged in']
      let shouldIgnore = false
      ignoreErrors.forEach((ignoreError) => {
        if (!error) return

        const errorIsInList = typeof error === 'string' && error.includes(ignoreError)
        const errorMessageIsInList = error.message?.includes(ignoreError)

        if (errorIsInList || errorMessageIsInList) {
          shouldIgnore = true
        }
      })
      if (shouldIgnore) return

      const errorId = error?.message || Date.now().toString()
      reactPlugin.trackException({ error, id: errorId })
    }

    window.addEventListener('unhandledrejection', logUnhandledErrors)
    return () => window.removeEventListener('unhandledrejection', logUnhandledErrors)
  }, [])

  useEffect(() => {
    const { setIsOnline } = useToastStore.getState()
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    setIsOnline(navigator.onLine)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    // if the subdomain or databaseSuffix changes, logout and reset store

    const currentSubdomain = getCompanyCodeFromUrl(isLocal, host)
    const currentDatabaseSuffix = getDatabaseSuffixFromUrl(isLocal)

    if (currentSubdomain.subdomain !== subdomain || currentDatabaseSuffix.databaseSuffix !== databaseSuffix) {
      console.error('change of key domain vars detected')
      navigate('/auth/logout')
    }
  }, [host, hash, pathname, subdomain, databaseSuffix, isLocal, navigate])

  useEffect(() => {
    // log route change to mixpanel. this is in useEffect so we only log it when it changes, not on every render
    mixpanelTrackPageView(mixpanelRoute, urlSegments, iframeSrc, routeLoaderData?.menuItemId)
  }, [mixpanelRoute, urlSegments, iframeSrc, routeLoaderData?.menuItemId])

  useEffect(() => {
    const { redirectTo, menuItemId } = routeLoaderData || {}

    if (redirectTo) {
      // stub routes (e.g. accounts -> accounts/client-ledger) must replace their own history
      // entry, not push a new one -- otherwise Back re-enters the stub, which immediately
      // redirects again, creating an inescapable loop (OS-2522)
      navigate(redirectTo, { replace: true })
    }

    // prevent users accessing non-permitted routes
    if (menuItemId && !allowedMenuIds.includes(menuItemId)) {
      throw new Error('You do not have permission to view this page', {
        cause: { status: 403 }
      })
    }

    updateHtmlTitle(breadcrumbs, params)
  }, [routeLoaderData, navigate, allowedMenuIds, breadcrumbs, params])

  return (
    <RouteGuard protectedRoute={!hash.includes('auth')}>
      <MUIStack
        sx={{
          width: '100%',
          height: '100%',
          cursor: isLoading ? 'wait' : 'default'
        }}
      >
        <GlobalLoadingBar />
        <CheckForLatestVersion />
        <ConfirmMountPoint />
        {!environmentName.includes('prod') && !isLocal && feedbackFormUrl && (
          <MUIButton
            variant="outlined"
            onClick={() => window.open(feedbackFormUrl, '_blank')}
            sx={{
              position: 'fixed',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1300,
              textTransform: 'none',
              backgroundColor: 'white'
            }}
          >
            Report issue
          </MUIButton>
        )}
        {hash.includes('#/auth/') ? <AuthLayout /> : <DefaultLayout />}
        <IframePage isVisible={iframeVisible} />
      </MUIStack>
    </RouteGuard>
  )
}

const updateHtmlTitle = (breadcrumbs, params) => {
  // update HTML page title
  const pageTitleParts = ['Osprey Approach']
  const pageName = breadcrumbs[breadcrumbs.length - 1]?.label || ''
  if (pageName) pageTitleParts.unshift(pageName)
  const { clientMatterNo } =
    useUrlStore.getState().urlDecodeClientMatterNo({ clientMatterNoParam: params?.clientMatterNo }) || {}
  if (clientMatterNo) pageTitleParts.unshift(clientMatterNo)
  document.title = pageTitleParts.join(' | ')
}

export const ErrorBoundary = PageErrorBoundary
